import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import clsx from 'clsx';
import { useMediaQuery } from 'react-responsive';
import { Title } from '../title';
import { CardContainer, ContainerBase } from '../containers';
import { Typography } from 'components/common/typography';
import { Button } from 'components/common';
import { customEvent } from '@/lib';
import AppRoutes from '@/const/routes';
import { HowWorksCard } from './HowWorksCard';
import Image from 'next/image';
const commonClass = '!font-semibold !text-2xl lg:!font-bold lg:!text-[32px]';

const initialItems = [
	{
		id: 1,
		title: (
			<Typography type="bold-30" className={commonClass}>
				Seamlessly upload your <span className="text-primary">documents</span>
			</Typography>
		),
		description: 'Every upload is protected, every step fully transparent.',
		image: '/img/landing/home/how-works/first-item.svg',
	},
	{
		id: 2,
		title: (
			<Typography type="bold-30" className={commonClass}>
				<span className="text-primary">Track your case</span> through every
				stage
			</Typography>
		),
		description:
			'Stay informed at every step—no more wondering where your case stands.',
		image: '/img/landing/home/how-works/second-item.svg',
	},
	{
		id: 3,
		title: (
			<Typography type="bold-30" className={commonClass}>
				Receive <span className="text-primary">direct deposits</span> to your
				accounts
			</Typography>
		),
		description:
			'No checks, no waiting—just instant digital deposits when your case closes.',
		image: '/img/landing/home/how-works/third-item.svg',
	},
	{
		id: 4,
		title: (
			<Typography type="bold-30" className={commonClass}>
				<span className="text-primary">Communicate directly</span> with your
				lawyer
			</Typography>
		),
		description: 'Real lawyers. Real conversations. Total transparency.',
		image: '/img/landing/home/how-works/fourth-item.png',
	},
	{
		id: 5,
		title: (
			<Typography type="bold-30" className={commonClass}>
				<span className="text-primary">Get matched</span> with open cases
			</Typography>
		),
		description: 'We connect you with open cases that match your profile.',
		image: '/img/landing/home/how-works/fifth-item.svg',
	},
	{
		id: 7,
		title: (
			<Typography type="bold-30" className={commonClass}>
				<span className="text-primary">AI</span> Chat
			</Typography>
		),
		description:
			'Smart guidance at every step—get instant answers and personalized help whenever you need it.',
		image: '/img/landing/home/how-works/sixth-item.svg',
	},
];

const mobileItems = [
	{
		id: 1,
		title: (
			<Typography type="bold-30" className={commonClass}>
				Seamlessly upload your <span className="text-primary">documents</span>
			</Typography>
		),
		description: 'Every upload is protected, every step fully transparent.',
		image: '/img/landing/home/how-works/first-item-mobile.svg',
	},
	{
		id: 2,
		title: (
			<Typography type="bold-30" className={commonClass}>
				Receive <span className="text-primary">direct deposits</span> to your
				accounts
			</Typography>
		),
		description:
			'No checks, no waiting—just instant digital deposits when your case closes.',
		image: '/img/landing/home/how-works/third-item.svg', //'/img/landing/home/how-works/third-item-mobile.svg',
	},
	{
		id: 3,
		title: (
			<Typography type="bold-30" className={commonClass}>
				<span className="text-primary">Get matched</span> with open cases
			</Typography>
		),
		description: 'We connect you with open cases that match your profile.',
		image: '/img/landing/home/how-works/third-item-mobile.svg', //'/img/landing/home/how-works/fifth-item-mobile.svg',
	},
	{
		id: 4,
		title: (
			<Typography type="bold-30" className={commonClass}>
				<span className="text-primary">Track your case</span> through every
				stage
			</Typography>
		),
		description:
			'Stay informed at every step—no more wondering where your case stands.',
		image: '/img/landing/home/how-works/fourth-item-mobile.svg',
	},

	{
		id: 6,
		title: (
			<Typography type="bold-30" className={commonClass}>
				<span className="text-primary">Communicate directly</span> with your
				lawyer
			</Typography>
		),
		description: 'Real lawyers. Real conversations. Total transparency.',
		image: '/img/landing/home/how-works/fourth-item.png', //'/img/landing/home/how-works/fourth-item-mobile.svg',
	},
	{
		id: 8,
		title: (
			<Typography type="bold-30" className={commonClass}>
				<span className="text-primary">AI</span> Chat
			</Typography>
		),
		description:
			'Smart guidance at every step—get instant answers and personalized help whenever you need it.',
		image: '/img/landing/home/how-works/sixth-item.svg',
	},
];

export const HowWorks: React.FC = () => {
	const router = useRouter();
	const [items, setItems] = useState(initialItems);

	const isDesktopOrLaptop = useMediaQuery({
		query: '(min-width: 1024px)',
	});

	useEffect(() => {
		if (!isDesktopOrLaptop) {
			setItems(mobileItems);
		}
	}, [isDesktopOrLaptop]);

	const steps = [
		{
			id: '01',
			text: 'See if you qualify in seconds',
		},
		{
			id: '02',
			text: 'Get updates on your case',
		},
		{
			id: '03',
			text: 'Get paid hassle free',
		},
	];
	return (
		<>
			<ContainerBase className=" px-0 md:px-5">
				<CardContainer className="bg-transparent flex flex-col items-center lg:space-y-16">
					<Title
						description={
							'Take control of your claim with tools that do the work for you'
						}
					>
						Your Claim, Simplified
					</Title>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 mt-8 lg:mt-0">
						{items.map(({ id, image, title, description }, index) => (
							<div
								key={id}
								className={clsx('mt-10 lg:mt-0 first:lg:mt-0', {
									'lg:!mt-[100px]': index % 2 !== 0,
								})}
							>
								<HowWorksCard
									image={image}
									title={title}
									description={description}
								/>
							</div>
						))}
					</div>
					<Button
						decoration="primary"
						size="large"
						className="!mt-16 lg:!w-[195px] px-[23px]"
						onClick={() => {
							customEvent({
								action: 'explore_all_cases_click',
								category: 'landing',
								label: 'Explore All Cases',
								value: 'Explore All Cases',
								callback: () => router.push(AppRoutes.LANDING_CASES),
							});
						}}
					>
						Explore Cases
					</Button>
				</CardContainer>
			</ContainerBase>
			<ContainerBase className=" px-0 md:px-5">
				<div className="bg-transparent pt-16 pb-12 md:pb-16 lg:pt-[48px]">
					<div className="bg-white py-10 px-8 lg:py-24 lg:px-20 border border-[#0D061614] rounded-[32px] lg:rounded-[64px] w-full lg:h-[680px]">
						<div className="flex flex-col lg:flex-row lg:gap-20">
							<div className="flex-1">
								<Typography
									type="custom-h2"
									className="font-bold !text-3xl lg:!text-5xl mb-0 lg:mb-0 h-[77px] flex items-center"
								>
									It’s easy to start
								</Typography>
								{steps.map(({ id, text }, index) => (
									<div
										key={id}
										className={clsx('flex items-center gap-8 h-[89px]', {
											'border-y border-primary': index === 1,
										})}
									>
										<Typography
											type="bold-36"
											className="text-green-base !text-4xl lg:!text-5xl"
										>
											{id}
										</Typography>
										<Typography
											type="regular-20"
											className="!text-base lg:!text-xl"
										>
											{text}
										</Typography>
									</div>
								))}
								<Button
									decoration="primary"
									size="large"
									className="mt-6 lg:mt-18 !w-full lg:!w-[162px] px-[23px] !h-12"
									onClick={() => {
										customEvent({
											action: 'explore_all_cases_click',
											category: 'landing',
											label: 'Explore All Cases',
											value: 'Explore All Cases',
											callback: () => router.push(AppRoutes.LANDING_CASES),
										});
									}}
								>
									Explore Cases
								</Button>
							</div>
							<div className="flex-1 h-[550px] relative -mt-14 hidden lg:block">
								<Image
									src={'img/landing/home/how-works/payment.svg'}
									alt=""
									fill
									className="object-contain object-center"
								/>
							</div>
						</div>
					</div>
				</div>
			</ContainerBase>
		</>
	);
};
